<script setup lang="ts">
import ProjektFeldField from '@/components/projekt-feld-field.vue'
import type { Projekt } from '@/models/apollon_conf_fk/Projekt'
import { ProjektFeld } from '@/models/apollon_conf_fk/ProjektFeld'
import type { ProjektFeldGruppe } from '@/models/apollon_conf_fk/ProjektFeldGruppe'
import { Data } from '@/models/apollon_data/Data'
import { GesuchPhase, type Gesuch } from '@/models/Gesuch'
import { createModel } from '@kp/js-model'
import dayjs from 'dayjs'
import { onMounted, ref, type Ref } from 'vue'

const {
    readOnly = false,
    gesuch,
    loading = false,
} = defineProps<{
    readOnly?: boolean
    gesuch: Gesuch
    loading?: boolean
}>()

const projekt: Ref<Projekt | null> = ref(null)

onMounted(() => {
    projekt.value = gesuch?.config?.config?.projekt || null
})

function isComponentVisible(comp: ProjektFeldGruppe | ProjektFeld | null) {
    let visible = true
    if (gesuch?.phase === GesuchPhase.EINREICHUNG) {
        if (comp?.einreichung_from) {
            visible = visible && dayjs(comp?.einreichung_from).isBefore(dayjs())
        }
        if (comp?.einreichung_to) {
            visible = visible && dayjs().isBefore(dayjs(comp?.einreichung_to).add(1, 'day'))
        }
        visible = visible && !!comp?.einreichung
        return visible
    } else if (gesuch?.phase === GesuchPhase.ABSCHLUSS) {
        if (comp?.abschluss_from) {
            visible = visible && dayjs(comp?.abschluss_from).isBefore(dayjs())
        }
        if (comp?.abschluss_to) {
            visible = visible && dayjs().isBefore(dayjs(comp?.abschluss_to).add(1, 'day'))
        }
        visible = visible && !!comp?.abschluss
        return visible
    } else if (gesuch?.phase === GesuchPhase.FERTIG) {
        return comp?.abschluss || comp?.einreichung
    }
    return false
}
</script>

<template>
    <v-container fluid>
        <v-alert
            v-if="projekt?.infotext"
            type="info"
            :icon="false"
            border="start"
            colored-border
            elevation="2"
        >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="projekt.infotext.replace('\n', '<br />')"> </span>
        </v-alert>
        <v-form no-gutters>
            <template v-for="gruppe in projekt?.feldgruppen" :key="gruppe.label">
                <v-card v-if="isComponentVisible(gruppe)" class="centered-card">
                    <v-card-title class="ml-4 mt-4">
                        {{ gruppe.label }}
                        <v-tooltip v-if="gruppe.hilfetext" bottom>
                            <template v-slot:activator="{ props }">
                                <v-icon color="primary" dark class="ml-4" v-bind="props">
                                    fas fa-info-circle
                                </v-icon>
                            </template>
                            <span>{{ gruppe.hilfetext }}</span>
                        </v-tooltip>
                    </v-card-title>
                    <v-card-text>
                        <v-container class="mx-0">
                            <template v-for="feld in gruppe.felder" :key="feld.feld_name">
                                <v-row v-if="isComponentVisible(feld)">
                                    <v-col cols="12" sm="6">
                                        <ProjektFeldField
                                            :model-value="
                                                gesuch.data?.projektdaten?.get(
                                                    feld.feld_name || 'null',
                                                )
                                            "
                                            :read-only="readOnly"
                                            :projektfeld="feld"
                                            :phase="gesuch.phase"
                                            @update:model-value="
                                                (value) => {
                                                    gesuch.data?.projektdaten?.set(
                                                        feld.feld_name || 'null',
                                                        value,
                                                    )
                                                    const data = createModel(Data, gesuch.data || undefined);
                                                    gesuch.set('data', data);
                                                }
                                            "
                                        ></ProjektFeldField>
                                    </v-col>
                                </v-row>
                            </template>
                        </v-container>
                    </v-card-text>
                </v-card>
            </template>
        </v-form>
        <v-card class="centered-card mt-5" variant="flat">
            <v-card-actions>
                <v-spacer />
                <v-btn
                    variant="flat"
                    class="mt-2"
                    color="action"
                    :loading="loading"
                    @click="$emit('next', null)"
                    append-icon="fas fa-arrow-right"
                >
                    {{ $t('next_step') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>
